import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountCircle,
  CheckCircle,
  Close,
  Email,
  ExpandLess,
  ExpandMore,
  Person,
  Phone,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '@/store/slices/authSlice';

interface ProfileCompletionBannerProps {
  onDismiss?: () => void;
  showDismiss?: boolean;
}

export const ProfileCompletionBanner: React.FC<ProfileCompletionBannerProps> = ({
  onDismiss,
  showDismiss = true
}) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!user || dismissed) {
    return null;
  }

  // Calculate profile completion
  const getProfileCompletionData = () => {
    const fields = [
      { key: 'fullName', label: 'Full Name', value: user.fullName, icon: <Person fontSize="small" /> },
      { key: 'email', label: 'Email', value: user.email, icon: <Email fontSize="small" />, verified: user.emailVerified },
      { key: 'phone', label: 'Phone', value: user.phone, icon: <Phone fontSize="small" />, verified: user.phoneVerified },
    ];

    const completedFields = fields.filter(field => field.value && field.value.trim() !== '');
    const percentage = Math.round((completedFields.length / fields.length) * 100);
    const missingFields = fields.filter(field => !field.value || field.value.trim() === '');
    const unverifiedFields = fields.filter(field => 
      field.value && 
      field.verified !== undefined && 
      !field.verified
    );

    return {
      percentage,
      completedFields,
      missingFields,
      unverifiedFields,
      isComplete: percentage === 100,
      hasUnverified: unverifiedFields.length > 0
    };
  };

  const profileData = getProfileCompletionData();

  // Don't show banner if profile is complete and everything is verified
  if (profileData.isComplete && !profileData.hasUnverified) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const handleCompleteProfile = () => {
    navigate('/profile');
  };

  const getSeverity = () => {
    if (profileData.percentage < 50) return 'error';
    if (profileData.percentage < 100 || profileData.hasUnverified) return 'warning';
    return 'success';
  };

  const getTitle = () => {
    if (profileData.percentage < 50) {
      return 'Complete Your Profile';
    }
    if (profileData.percentage < 100) {
      return 'Almost Done! Complete Your Profile';
    }
    if (profileData.hasUnverified) {
      return 'Verify Your Information';
    }
    return 'Profile Complete';
  };

  const getMessage = () => {
    if (profileData.percentage < 50) {
      return 'Complete your profile to unlock all features and improve account security.';
    }
    if (profileData.percentage < 100) {
      return 'Add the missing information to complete your profile.';
    }
    if (profileData.hasUnverified) {
      return 'Verify your email and phone number for enhanced security.';
    }
    return 'Your profile is complete and verified!';
  };

  return (
    <Alert 
      severity={getSeverity()}
      variant="filled"
      sx={{ 
        mb: 2,
        '& .MuiAlert-message': { width: '100%' }
      }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            color="inherit"
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            sx={{ textTransform: 'none' }}
          >
            Details
          </Button>
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={handleCompleteProfile}
            startIcon={<AccountCircle />}
            sx={{ 
              textTransform: 'none',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            Complete Profile
          </Button>
          {showDismiss && (
            <Button
              color="inherit"
              size="small"
              onClick={handleDismiss}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <Close fontSize="small" />
            </Button>
          )}
        </Box>
      }
    >
      <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getTitle()}
        <Chip
          label={`${profileData.percentage}% Complete`}
          size="small"
          color={profileData.percentage >= 75 ? 'success' : 'default'}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            color: 'inherit',
            fontWeight: 600
          }}
        />
      </AlertTitle>

      <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
        {getMessage()}
      </Typography>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={profileData.percentage}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: profileData.percentage >= 75 ? 'success.main' : 'warning.main'
            }
          }}
        />
      </Box>

      {/* Expandable Details */}
      <Collapse in={expanded}>
        <Box sx={{ pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
          {/* Missing Fields */}
          {profileData.missingFields.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning fontSize="small" />
                Missing Information:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {profileData.missingFields.map((field) => (
                  <Chip
                    key={field.key}
                    icon={field.icon}
                    label={field.label}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'inherit'
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Unverified Fields */}
          {profileData.unverifiedFields.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle fontSize="small" />
                Need Verification:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {profileData.unverifiedFields.map((field) => (
                  <Chip
                    key={field.key}
                    icon={field.icon}
                    label={`${field.label} (Unverified)`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'inherit'
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Completed Fields */}
          {profileData.completedFields.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle fontSize="small" />
                Completed:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {profileData.completedFields.map((field) => (
                  <Chip
                    key={field.key}
                    icon={field.icon}
                    label={field.label}
                    size="small"
                    color="success"
                    sx={{
                      bgcolor: 'rgba(76, 175, 80, 0.2)',
                      color: 'inherit'
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Alert>
  );
};